import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import type { Profile } from 'api/api.types'
import { APIService } from 'api/APIService'
import { getProfile, updateProfileField } from 'src/store/app.state.store'
import { getCities } from 'src/store/data.store'
import { cleanPhone } from 'utils/cleanPhone'
import { validateEmail } from 'utils/validateEmail'
import { dateMask, focusMaskStart, phoneInputMask } from 'utils/masks'
import { ShadowInput } from 'components/FormElements/ShadowInput/ShadowInput'
import { ShadowSelect } from 'components/FormElements/ShadowSelect/ShadowSelect'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { Modal } from 'components/Utility/Modal/Modal'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { ChangePasswordForm } from '../ChangePasswordForm/ChangePasswordForm'
import formStyles from './ProfileEditForm.module.scss'
import styles from '../Form.module.scss'

const SEX_OPTIONS = [{ id: 2, name: 'Мужской' }, { id: 3, name: 'Женский' }]
const SAVE_DELAY = 300
const DATE_FORMAT = 'DD.MM.YYYY'

type FormData = {
  defaultCityId: number | null,
  firstName: string,
  lastName: string,
  sex: number,
  /** Digits only, with the leading 7 */
  phone: string,
  email: string,
  /** As typed, DD.MM.YYYY */
  birthday: string,
}
type Field = keyof FormData

const formFromProfile = (profile: Profile | null): FormData => ({
  defaultCityId: profile?.defaultCityId ?? null,
  firstName: profile?.firstName ?? '',
  lastName: profile?.lastName ?? '',
  sex: profile?.sex ?? 1,
  phone: profile?.phone ?? '',
  email: profile?.email ?? '',
  birthday: profile?.birthday ? moment(profile.birthday).format(DATE_FORMAT) : '',
})

/** Throws the message to show under the field */
const validateField = (field: Field, value: FormData[Field]) => {
  const text = String(value ?? '')
  if (field === 'firstName' && !text) {
    throw new Error('Обязательное поле')
  }
  if (field === 'phone' && text.length !== 11) {
    throw new Error('Неверный формат номера')
  }
  if (field === 'email' && text.length && !validateEmail(text)) {
    throw new Error('Неверный формат email')
  }
  if (field === 'birthday' && text.length) {
    const date = moment(text, DATE_FORMAT)
    if (text.replace(/\D/g, '').length !== 8 || !date.isValid()) {
      throw new Error('Неверный формат даты')
    }
    if (date.isAfter(moment())) {
      throw new Error('Неверная дата рождения')
    }
  }
}

/** «Личные данные»: every field is saved on its own shortly after it changes */
export const ProfileEditForm: React.FC = () => {
  const dispatch = useDispatch()
  const cities = useSelector(getCities)
  const profile = useSelector(getProfile)
  const [formData, setFormData] = useState(() => formFromProfile(profile))
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false)
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null)
  const timers = useRef<Partial<Record<Field, ReturnType<typeof setTimeout>>>>({})

  useEffect(() => {
    setFormData(formFromProfile(profile))
  }, [profile])

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), [])

  const setError = (field: Field, message: string) => setErrors((current) => ({ ...current, [field]: message }))

  const setField = <K extends Field>(field: K, value: FormData[K]) => {
    setFormData((current) => ({ ...current, [field]: value }))
    clearTimeout(timers.current[field])
    setError(field, '')
    timers.current[field] = setTimeout(async () => {
      try {
        validateField(field, value)
        const saved = field === 'birthday'
          ? (value ? moment(value as string, DATE_FORMAT).format('YYYY-MM-DD') : null)
          : value
        await APIService.profile.editProfile({ [field]: saved })
        dispatch(updateProfileField({ field, value: saved as Profile[K] }))
      } catch (e) {
        setError(field, (e as Error).message)
      }
    }, SAVE_DELAY)
  }

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (cleanPhone(e.target.value) === '7') {
      setField('phone', '')
    }
  }

  const handlePasswordChanged = useCallback(() => {
    setChangePasswordOpen(false)
    setChangePasswordSuccess(true)
  }, [])

  const handlePasswordError = useCallback((e: Error) => setChangePasswordError(e.message), [])

  const errorProps = (field: Field) => ({ error: !!errors[field], tooltip: errors[field] })

  return (
    <div className={styles.form}>
      <div className={styles.fields}>
        <ShadowSelect
          onChange={(value) => setField('defaultCityId', value)}
          value={formData.defaultCityId ?? ''}
          className={styles.field}
          shadowColor={'transparent'}
          placeholder={'Город*'}
          options={cities.map(({ city }) => city)}
        />
        <ShadowInput
          onChange={(value) => setField('firstName', value)}
          value={formData.firstName}
          className={styles.field}
          shadowColor={'transparent'}
          placeholder={'Имя*'}
          {...errorProps('firstName')}
        />
        <ShadowInput
          onChange={(value) => setField('lastName', value)}
          value={formData.lastName}
          className={styles.field}
          shadowColor={'transparent'}
          placeholder={'Фамилия'}
          {...errorProps('lastName')}
        />
        <ShadowSelect
          onChange={(value) => setField('sex', value)}
          value={formData.sex}
          className={styles.field}
          shadowColor={'transparent'}
          placeholder={'Пол'}
          options={SEX_OPTIONS}
        />
        <ShadowInput
          onFocus={focusMaskStart}
          onBlur={handlePhoneBlur}
          onChange={(value) => setField('phone', cleanPhone(value))}
          value={formData.phone.replace(/^7/, '')}
          type={'tel'}
          mask={phoneInputMask}
          className={styles.field}
          shadowColor={'transparent'}
          placeholder={'Мобильный телефон *'}
          {...errorProps('phone')}
        />
        <ShadowInput
          onChange={(value) => setField('email', value)}
          value={formData.email}
          type={'email'}
          className={styles.field}
          shadowColor={'transparent'}
          placeholder={'Email *'}
          {...errorProps('email')}
        />
        <ShadowInput
          onFocus={focusMaskStart}
          onChange={(value) => setField('birthday', value)}
          value={formData.birthday}
          mask={dateMask}
          type={'text'}
          className={styles.field}
          shadowColor={'transparent'}
          placeholder={'Дата рождения'}
          {...errorProps('birthday')}
        />
        <AppButton className={formStyles.changePassword} onClick={() => setChangePasswordOpen(true)}>
          Изменить пароль
        </AppButton>
      </div>
      <Modal
        className={formStyles.changePasswordModal}
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      >
        <ChangePasswordForm onSuccess={handlePasswordChanged} onError={handlePasswordError} />
      </Modal>
      <StatusModal
        isOpen={changePasswordSuccess}
        status={'success'}
        modalText={'Пароль успешно изменен'}
        onClose={() => setChangePasswordSuccess(false)}
        onButtonClick={() => setChangePasswordSuccess(false)}
        buttonText={'OK'}
      />
      <StatusModal
        isOpen={changePasswordError !== null}
        status={'error'}
        modalText={changePasswordError}
        onClose={() => setChangePasswordError(null)}
        onButtonClick={() => setChangePasswordError(null)}
        buttonText={'ОК'}
      />
    </div>
  )
}
